// ╔══════════════════════════════════════════════════════════════╗
// ║  Employee Profile — Tabs layout matching the original design  ║
// ║  Hero + KPIs + 7 tabs (overview/salary/attendance/advances/   ║
// ║  bonuses/deductions/documents)                                ║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../components/Toast.jsx';
import {
  fmtAR,
  fmtDate,
  fmtDateShort,
  fmtMoney,
  STATUS_LABEL_AR,
} from '../lib/format.js';
import { Avatar, Badge, Btn, Card, Field, Input, KPI, Modal, Select } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';
import { BarChart, DonutChart, LineChart } from '../components/Charts.jsx';
import ExitModal from '../components/ExitModal.jsx';

const STATUS_TONES = {
  active: 'success',
  leave: 'info',
  probation: 'warn',
  terminated: 'neutral',
};

const TABS = [
  { id: 'overview', l: 'نظرة عامة', i: 'dashboard', acc: ['admin', 'acc', 'eng'] },
  { id: 'salary', l: 'الراتب', i: 'wallet', acc: ['admin', 'acc'] },
  { id: 'attendance', l: 'الحضور', i: 'fingerprint', acc: ['admin', 'acc'] },
  { id: 'advances', l: 'السلف', i: 'briefcase', acc: ['admin', 'acc'] },
  { id: 'bonuses', l: 'المكافآت', i: 'award', acc: ['admin', 'acc'] },
  { id: 'deductions', l: 'الخصومات', i: 'trendD', acc: ['admin', 'acc'] },
  { id: 'documents', l: 'المستندات', i: 'file', acc: ['admin', 'acc'] },
];

export default function ProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [exiting, setExiting] = useState(false);

  const isAdmin = user?.role === 'admin';
  // admin + acc can edit basic data (incl. base salary). Only admin can document exits.
  const canEdit = user?.role === 'admin' || user?.role === 'acc';
  const role = user?.role || 'admin';
  const visibleTabs = TABS.filter((t) => t.acc.includes(role));

  const { data, isLoading, error } = useQuery({
    queryKey: ['employee', id, 'profile'],
    queryFn: () => api.get(`/employees/${id}/profile`).then((r) => r.data),
  });

  const { data: deptsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get('/departments').then((r) => r.data),
    enabled: editing,
  });
  const { data: shiftsData } = useQuery({
    queryKey: ['shifts'],
    queryFn: () => api.get('/shifts').then((r) => r.data),
    enabled: editing,
  });

  const saveMut = useMutation({
    mutationFn: (payload) => api.patch(`/employees/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employee', id, 'profile'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
      toast.push({ tone: 'success', title: 'تم تحديث بيانات الموظف' });
      setEditing(false);
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل الحفظ', body: getErrorMessage(err) }),
  });

  function refreshProfile() {
    qc.invalidateQueries({ queryKey: ['employee', id, 'profile'] });
  }

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8">
        <Card className="p-12 text-center text-ink-400">
          <div className="w-12 h-12 mx-auto rounded-full border-4 border-brand-200 border-t-brand-500 animate-spin" />
          <div className="mt-3">جاري التحميل…</div>
        </Card>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="p-6 lg:p-8">
        <Card className="p-12 text-center">
          <Icon name="alert" size={48} className="mx-auto text-rose-400 mb-3" />
          <h3 className="font-bold text-lg">لم يتم العثور على الموظف</h3>
          <Btn variant="outline" icon="arrowR" className="mt-4" onClick={() => navigate('/employees')}>
            رجوع للقائمة
          </Btn>
        </Card>
      </div>
    );
  }

  const { employee, attendance, financial, production, kpis } = data;
  const canSeeFinancial = role === 'admin' || role === 'acc';

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      {/* Breadcrumbs */}
      <nav className="text-xs text-ink-400 flex items-center gap-1.5 flex-wrap">
        <Link to="/" className="hover:text-brand-500">الرئيسية</Link>
        <Icon name="chevL" size={11} />
        <Link to="/employees" className="hover:text-brand-500">الموظفون</Link>
        <Icon name="chevL" size={11} />
        <span className="text-ink-700 dark:text-ink-100 font-bold truncate">{employee.name}</span>
      </nav>

      {/* Hero */}
      <Card className="p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Avatar name={employee.name} size={80} className="text-2xl shadow-pop" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-black truncate">{employee.name}</h1>
              <Badge tone={STATUS_TONES[employee.status]} dot>
                {STATUS_LABEL_AR[employee.status]}
              </Badge>
            </div>
            <div className="text-sm text-ink-500 dark:text-ink-300 mt-1">
              <span className="font-mono">{employee.id}</span>
              <span className="mx-2 text-ink-300">·</span>
              <span>{employee.position}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Badge tone="brand">{employee.department?.nameAr ?? employee.deptId}</Badge>
              <Badge tone="neutral">وردية {employee.shift?.nameAr ?? employee.shiftId}</Badge>
              {production && <Badge tone="info">عامل إنتاج · {production.stage?.nameAr}</Badge>}
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            {canEdit && (
              <Btn variant="outline" icon="edit" onClick={() => setEditing(true)}>تعديل</Btn>
            )}
            {isAdmin && employee.status !== 'terminated' && (
              <Btn variant="ghost" icon="x" onClick={() => setExiting(true)} title="توثيق خروج">
                خروج
              </Btn>
            )}
            <Btn variant="ghost" icon="arrowR" onClick={() => navigate('/employees')}>رجوع</Btn>
          </div>
        </div>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="معدل الحضور" value={`${kpis.attendanceRate}%`} icon="check" accent
             sub={`${fmtAR(attendance.summary.thisMonthPresent)} يوم هذا الشهر`}/>
        <KPI label="مدة الخدمة" value={`${fmtAR(kpis.tenureYears)}`} sub="سنة" icon="award"/>
        {canSeeFinancial && (
          <KPI label="الراتب الإجمالي"
               value={fmtAR(Number(employee.baseSalary) + Number(employee.allowance))}
               sub="جنيه شهرياً" icon="wallet"/>
        )}
        {canSeeFinancial && (
          <KPI label="السلف النشطة" value={fmtAR(kpis.activeAdvances)}
               sub={kpis.activeAdvances > 0 ? 'تستقطع من الراتب' : 'لا توجد سلف'} icon="briefcase"/>
        )}
      </div>

      {/* Tabs nav */}
      <div className="flex items-center gap-1 border-b border-ink-100 dark:border-ink-800 overflow-x-auto no-scrollbar">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 h-10 text-sm font-semibold border-b-2 -mb-px transition whitespace-nowrap inline-flex items-center gap-1.5 ${
              tab === t.id
                ? 'border-brand-500 text-brand-600 dark:text-brand-300'
                : 'border-transparent text-ink-400 hover:text-ink-700 dark:hover:text-ink-100'
            }`}
          >
            <Icon name={t.i} size={14} />
            {t.l}
            {t.id === 'advances' && financial?.allAdvances && (
              <span className="text-[10px] tabular text-ink-400">({fmtAR(financial.allAdvances.length)})</span>
            )}
            {t.id === 'bonuses' && financial?.recentBonuses && (
              <span className="text-[10px] tabular text-ink-400">({fmtAR(financial.recentBonuses.length)})</span>
            )}
            {t.id === 'deductions' && financial?.recentDeductions && (
              <span className="text-[10px] tabular text-ink-400">({fmtAR(financial.recentDeductions.length)})</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <OverviewTab
          employee={employee}
          attendance={attendance}
          production={production}
          canSeeFinancial={canSeeFinancial}
          canEdit={canEdit}
          onEdit={() => setEditing(true)}
        />
      )}
      {tab === 'salary' && canSeeFinancial && <SalaryTab employee={employee} financial={financial} />}
      {tab === 'attendance' && <AttendanceTab attendance={attendance} kpis={kpis} />}
      {tab === 'advances' && canSeeFinancial && <AdvancesTab employee={employee} advances={financial.allAdvances ?? []} onChange={refreshProfile} />}
      {tab === 'bonuses' && canSeeFinancial && <BonusesTab employee={employee} bonuses={financial.recentBonuses ?? []} onChange={refreshProfile} />}
      {tab === 'deductions' && canSeeFinancial && <DeductionsTab employee={employee} deductions={financial.recentDeductions ?? []} onChange={refreshProfile} />}
      {tab === 'documents' && <DocumentsTab />}

      {/* Edit Modal */}
      {editing && (
        <EditModal
          employee={employee}
          departments={deptsData?.departments ?? []}
          shifts={shiftsData?.shifts ?? []}
          onClose={() => setEditing(false)}
          onSave={(payload) => saveMut.mutate(payload)}
          loading={saveMut.isPending}
        />
      )}

      {/* Exit Modal */}
      {exiting && (
        <ExitModal
          employee={employee}
          onClose={() => setExiting(false)}
          onSuccess={() => setExiting(false)}
        />
      )}
    </div>
  );
}

// ─── Overview Tab ───
function OverviewTab({ employee, attendance, production, canSeeFinancial, canEdit, onEdit }) {
  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-bold text-base flex items-center gap-2">
            <Icon name="users" size={16} />
            معلومات شخصية
          </h3>
          {canEdit && (
            <Btn variant="outline" size="sm" icon="edit" onClick={onEdit}>
              تعديل المعلومات الشخصية
            </Btn>
          )}
        </div>
        <InfoGrid items={[
          { label: 'الاسم', value: employee.name },
          { label: 'النوع', value: employee.gender === 'M' ? 'ذكر' : 'أنثى' },
          { label: 'الرقم القومي', value: employee.nid || '—', mono: true },
          { label: 'الهاتف', value: employee.phone || '—', mono: true },
          { label: 'العنوان', value: employee.address || '—' },
          { label: 'تاريخ التعيين', value: fmtDate(employee.hiredOn) },
          { label: 'الفرع', value: employee.branch || '—' },
          { label: 'مدة الخدمة', value: `${fmtAR(employee.tenureYears)} سنة` },
        ]}/>
      </Card>

      <Card className="p-5 space-y-3">
        <h3 className="font-bold text-base flex items-center gap-2">
          <Icon name="briefcase" size={16} />
          بيانات وظيفية
        </h3>
        <InfoGrid items={[
          { label: 'القسم', value: employee.department?.nameAr ?? employee.deptId },
          { label: 'الوردية', value: employee.shift?.nameAr ?? employee.shiftId },
          { label: 'المسمى', value: employee.position },
          { label: 'الحالة', value: STATUS_LABEL_AR[employee.status] },
          ...(canSeeFinancial ? [
            { label: 'الراتب الأساسي', value: fmtMoney(employee.baseSalary) },
            { label: 'البدلات', value: fmtMoney(employee.allowance) },
            { label: 'التأمين', value: fmtMoney(employee.insuranceAmount) },
          ] : []),
        ]}/>
      </Card>

      <Card className="p-5">
        <h3 className="font-bold text-base mb-3">حضور آخر 6 شهور</h3>
        <div className="text-ink-700 dark:text-ink-200">
          <LineChart
            data={attendance.trend6Months.map((m) => ({ label: m.label.split(' ')[0], value: m.presentDays }))}
            color="#cc9c46"
          />
        </div>
      </Card>

      {production && (
        <Card className="p-5">
          <h3 className="font-bold text-base mb-3 flex items-center gap-2">
            <Icon name="factory" size={16} />
            ملخص الإنتاج هذا الشهر
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="حقّق الهدف" value={fmtAR(production.thisMonth.hits)} sub="يوم" tone="success"/>
            <MiniStat label="تحت الهدف" value={fmtAR(production.thisMonth.misses)} sub="يوم" tone="warn"/>
            <MiniStat label="منتج كلي" value={fmtAR(production.thisMonth.totalProduced)} sub="قطعة" tone="brand"/>
            <MiniStat label="صافي المكافأة" value={fmtAR(production.thisMonth.bonusTotal - production.thisMonth.penaltyTotal)}
              sub="جنيه" tone={production.thisMonth.bonusTotal - production.thisMonth.penaltyTotal >= 0 ? 'success' : 'danger'}/>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Salary Tab ───
function SalaryTab({ employee, financial }) {
  const base = Number(employee.baseSalary);
  const allowance = Number(employee.allowance);
  const insurance = Number(employee.insuranceAmount);
  const tax = Math.round(base * 0.05);

  const lastSlip = financial.payslipsTrend[financial.payslipsTrend.length - 1];

  const earnings = [
    ['الراتب الأساسي', base],
    ['البدلات', allowance],
    ...(lastSlip ? [
      ['العمل الإضافي', Number(lastSlip.overtimeAmount)],
      ['مكافأة الإنتاج', Number(lastSlip.productionBonus)],
      ['مكافآت أخرى', Number(lastSlip.otherBonuses)],
    ] : []),
  ].filter(([, v]) => v > 0);

  const deductionsList = [
    ['التأمين الاجتماعي', insurance],
    ['ضريبة كسب العمل', tax],
    ...(lastSlip ? [
      ['خصم غياب', Number(lastSlip.absenceDeduction)],
      ['خصم تأخير', Number(lastSlip.lateDeduction)],
      ['سداد سلفة', Number(lastSlip.advanceDeduction)],
      ['خصومات أخرى', Number(lastSlip.otherDeductions)],
    ] : []),
  ].filter(([, v]) => v > 0);

  const totalEarn = earnings.reduce((s, [, v]) => s + v, 0);
  const totalDed = deductionsList.reduce((s, [, v]) => s + v, 0);
  const net = lastSlip ? Number(lastSlip.net) : totalEarn - totalDed;

  return (
    <Card className="p-5 space-y-4">
      <h3 className="font-bold text-base">
        تفصيل الراتب الشهري
        {lastSlip?.month && (
          <span className="text-ink-400 text-sm font-normal mr-2">
            (آخر كشف: {new Date(lastSlip.month).toLocaleDateString('ar-EG', { month: 'long', year: 'numeric' })})
          </span>
        )}
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-5">
        <table className="w-full text-sm">
          <tbody className="divide-soft">
            <tr className="font-bold bg-emerald-50 dark:bg-emerald-950/30">
              <td className="py-2 px-3 text-emerald-700 dark:text-emerald-300">المستحقات</td>
              <td></td>
            </tr>
            {earnings.map(([k, v]) => (
              <tr key={k}>
                <td className="py-2 px-3 text-ink-600 dark:text-ink-300">{k}</td>
                <td className="py-2 px-3 text-end tabular font-semibold text-emerald-600">
                  + {fmtAR(v)} EGP
                </td>
              </tr>
            ))}
            <tr className="font-bold bg-rose-50 dark:bg-rose-950/30">
              <td className="py-2 px-3 text-rose-700 dark:text-rose-300">الخصومات</td>
              <td></td>
            </tr>
            {deductionsList.map(([k, v]) => (
              <tr key={k}>
                <td className="py-2 px-3 text-ink-600 dark:text-ink-300">{k}</td>
                <td className="py-2 px-3 text-end tabular font-semibold text-rose-600">
                  − {fmtAR(v)} EGP
                </td>
              </tr>
            ))}
            <tr className="bg-brand-50 dark:bg-brand-950/30">
              <td className="py-3 px-3 font-bold">صافي الراتب</td>
              <td className="py-3 px-3 text-end tabular font-extrabold text-lg text-brand-700 dark:text-brand-300">
                {fmtAR(net)} EGP
              </td>
            </tr>
          </tbody>
        </table>

        <div className="text-ink-700 dark:text-ink-200">
          <DonutChart
            size={200}
            thickness={28}
            centerLabel="إجمالي"
            centerValue={fmtAR(totalEarn)}
            data={[
              { label: 'الأساسي', value: base, color: '#cc9c46' },
              { label: 'البدلات', value: allowance, color: '#d8b46c' },
              ...(lastSlip ? [
                { label: 'الإضافي', value: Number(lastSlip.overtimeAmount), color: '#10b981' },
                { label: 'مكافآت', value: Number(lastSlip.productionBonus) + Number(lastSlip.otherBonuses), color: '#0ea5e9' },
              ] : []),
            ].filter((s) => s.value > 0)}
          />
        </div>
      </div>
    </Card>
  );
}

// ─── Attendance Tab ───
function AttendanceTab({ attendance, kpis }) {
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h3 className="font-bold text-base mb-3">إحصاءات الحضور</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="الحاضر" value={fmtAR(attendance.summary.thisMonthPresent)} sub="يوم هذا الشهر"/>
          <MiniStat label="تأخير" value={fmtAR(attendance.summary.thisMonthLate)} sub={`${fmtAR(attendance.summary.thisMonthLateMinutes)} دقيقة`} tone="warn"/>
          <MiniStat label="إضافي" value={`${(attendance.summary.thisMonthOtMinutes / 60).toFixed(1)}س`} sub="ساعات" tone="info"/>
          <MiniStat label="المعدل العام" value={`${kpis.attendanceRate}%`} sub="حضور" tone="success"/>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-bold text-base mb-3">حضور آخر 6 شهور</h3>
        <div className="text-ink-700 dark:text-ink-200">
          <LineChart
            data={attendance.trend6Months.map((m) => ({ label: m.label.split(' ')[0], value: m.presentDays }))}
            color="#cc9c46"
            height={200}
          />
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-bold text-base mb-3">ساعات الإضافي شهرياً</h3>
        <div className="text-ink-700 dark:text-ink-200">
          <BarChart
            data={attendance.trend6Months.map((m) => ({
              label: m.label.split(' ')[0],
              value: Math.round(m.otMinutes / 60),
            }))}
            color="#0ea5e9"
            height={160}
          />
        </div>
      </Card>
    </div>
  );
}

// ─── Advances Tab ───
function AdvancesTab({ employee, advances, onChange }) {
  return (
    <FinancialListTab
      employee={employee}
      kind="advances"
      title="السلف والقروض"
      addLabel="إضافة سلفة"
      emptyMsg="لا توجد سلف مسجّلة"
      items={advances}
      itemColumns={[
        { key: 'id', label: '#', mono: true },
        { key: 'amount', label: 'المبلغ', money: true },
        { key: 'reason', label: 'السبب' },
        { key: 'monthlyDeduction', label: 'القسط', money: true },
        { key: 'installments_progress', label: 'الأقساط',
          render: (a) => `${fmtAR(a.paidInstallments)} / ${fmtAR(a.installments)}` },
        { key: 'status', label: 'الحالة', badge: true },
      ]}
      addFields={[
        { key: 'amount', label: 'المبلغ (جنيه)', type: 'number', required: true, min: 1 },
        { key: 'installments', label: 'عدد الأقساط', type: 'number', min: 1, max: 24, default: 1 },
        { key: 'reason', label: 'السبب', type: 'select',
          options: ['ظروف اجتماعية', 'علاج طبي', 'مصاريف تعليم', 'إصلاح منزل', 'مناسبة عائلية', 'أخرى'] },
        { key: 'requestedAt', label: 'التاريخ', type: 'date', default: new Date().toISOString().slice(0, 10) },
      ]}
      onChange={onChange}
    />
  );
}

// ─── Bonuses Tab ───
function BonusesTab({ employee, bonuses, onChange }) {
  return (
    <FinancialListTab
      employee={employee}
      kind="bonuses"
      title="المكافآت"
      addLabel="إضافة مكافأة"
      emptyMsg="لا توجد مكافآت مسجّلة"
      items={bonuses}
      itemColumns={[
        { key: 'id', label: '#', mono: true },
        { key: 'amount', label: 'المبلغ', money: true, positive: true },
        { key: 'type', label: 'النوع' },
        { key: 'month_display', label: 'الشهر',
          render: (b) => b.month ? new Date(b.month).toLocaleDateString('ar-EG', { month: 'short', year: 'numeric' }) : '—' },
        { key: 'status', label: 'الحالة', badge: true },
      ]}
      addFields={[
        { key: 'amount', label: 'المبلغ (جنيه)', type: 'number', required: true, min: 1 },
        { key: 'type', label: 'النوع', type: 'select',
          options: ['مكافأة أداء', 'بدل عيد', 'حافز كفاءة', 'مكافأة انتظام', 'مكافأة إضافة', 'أخرى'] },
        { key: 'month', label: 'الشهر', type: 'month', default: new Date().toISOString().slice(0, 7) },
        { key: 'note', label: 'ملاحظة', type: 'text' },
      ]}
      onChange={onChange}
    />
  );
}

// ─── Deductions Tab ───
function DeductionsTab({ employee, deductions, onChange }) {
  return (
    <FinancialListTab
      employee={employee}
      kind="deductions"
      title="الخصومات"
      addLabel="إضافة خصم"
      emptyMsg="لا توجد خصومات مسجّلة"
      items={deductions}
      itemColumns={[
        { key: 'id', label: '#', mono: true },
        { key: 'amount', label: 'المبلغ', money: true, negative: true },
        { key: 'type', label: 'النوع' },
        { key: 'month_display', label: 'الشهر',
          render: (d) => d.month ? new Date(d.month).toLocaleDateString('ar-EG', { month: 'short', year: 'numeric' }) : '—' },
        { key: 'status', label: 'الحالة', badge: true },
      ]}
      addFields={[
        { key: 'amount', label: 'المبلغ (جنيه)', type: 'number', required: true, min: 1 },
        { key: 'type', label: 'النوع', type: 'select',
          options: ['خصم تأخير', 'خصم غياب', 'خصم مخالفة', 'خصم تلف معدات', 'أخرى'] },
        { key: 'month', label: 'الشهر', type: 'month', default: new Date().toISOString().slice(0, 7) },
        { key: 'note', label: 'ملاحظة', type: 'text' },
      ]}
      onChange={onChange}
    />
  );
}

// ─── Documents Tab ───
function DocumentsTab() {
  return (
    <Card className="p-12 text-center">
      <Icon name="file" size={48} className="mx-auto text-ink-300 mb-3" />
      <h3 className="font-bold text-ink-500">قسم المستندات</h3>
      <p className="text-sm text-ink-400 mt-2">قريباً — هنا تظهر العقود وصور بطاقة الرقم القومي والشهادات.</p>
    </Card>
  );
}

// ─── Reusable Financial List Tab (Advances / Bonuses / Deductions) ───
// Excel import has been moved to a dedicated "الرفع" page. Tabs only support
// inline + Add / Edit / Delete now.
function FinancialListTab({ employee, kind, title, addLabel, emptyMsg, items, itemColumns, addFields, onChange }) {
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // the item being edited (or null)
  const [deleting, setDeleting] = useState(null); // the item to delete
  const emptyForm = () =>
    Object.fromEntries(addFields.map((f) => [f.key, f.default ?? '']));
  const [form, setForm] = useState(emptyForm);

  const addMut = useMutation({
    mutationFn: (payload) => api.post(`/${kind}`, { ...payload, employeeId: employee.id }),
    onSuccess: () => {
      toast.push({ tone: 'success', title: `تم إضافة ${title}` });
      setShowAdd(false);
      setForm(emptyForm());
      onChange();
    },
    onError: (err) => toast.push({ tone: 'danger', title: 'فشل', body: getErrorMessage(err) }),
  });

  const editMut = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/${kind}/${id}`, payload),
    onSuccess: () => {
      toast.push({ tone: 'success', title: 'تم حفظ التعديلات' });
      setEditing(null);
      onChange();
    },
    onError: (err) => toast.push({ tone: 'danger', title: 'فشل التعديل', body: getErrorMessage(err) }),
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/${kind}/${id}`),
    onSuccess: () => {
      toast.push({ tone: 'success', title: 'تم الحذف' });
      setDeleting(null);
      onChange();
    },
    onError: (err) => toast.push({ tone: 'danger', title: 'فشل الحذف', body: getErrorMessage(err) }),
  });

  function handleAdd(e) {
    e.preventDefault();
    const payload = { ...form };
    addMut.mutate(payload);
  }

  function openEdit(item) {
    // Prefill the form from the item — convert stored month '2026-05-01' → '2026-05' for the month input.
    const prefilled = Object.fromEntries(
      addFields.map((f) => {
        let v = item[f.key];
        if (f.type === 'month' && v) v = String(v).slice(0, 7);
        if (f.type === 'date' && v) v = String(v).slice(0, 10);
        return [f.key, v ?? f.default ?? ''];
      }),
    );
    setEditing({ item, form: prefilled });
  }

  function handleEditSubmit(e) {
    e.preventDefault();
    if (!editing) return;
    editMut.mutate({ id: editing.item.id, payload: editing.form });
  }

  function exportToCSV() {
    if (items.length === 0) {
      toast.push({ tone: 'warn', title: 'لا توجد بيانات للتصدير' });
      return;
    }
    const headers = itemColumns.map((c) => c.label).filter((l) => l !== '#' && l !== 'الحالة');
    const lines = [headers.join(',')];
    for (const item of items) {
      lines.push(
        itemColumns
          .filter((c) => c.label !== '#' && c.label !== 'الحالة')
          .map((c) => {
            const v = c.render ? c.render(item) : item[c.key];
            return `"${String(v ?? '').replace(/"/g, '""')}"`;
          })
          .join(','),
      );
    }
    const csv = '﻿' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kind}_${employee.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-bold text-base">{title}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Btn variant="outline" size="sm" icon="download" onClick={exportToCSV}>تصدير Excel</Btn>
          <Btn variant="primary" size="sm" icon="plus" onClick={() => setShowAdd((v) => !v)}>
            {addLabel}
          </Btn>
        </div>
      </div>
      <p className="text-[11px] text-ink-400 -mt-2">
        💡 للاستيراد بالـ Excel لكل الموظفين دفعة واحدة، استخدم صفحة <strong>الرفع</strong> من القائمة الجانبية.
      </p>

      {showAdd && (
        <Card className="p-5 ring-1 ring-brand-300 dark:ring-brand-700 fade-up">
          <h4 className="font-bold mb-4 text-brand-600 dark:text-brand-300">{addLabel}</h4>
          <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {addFields.map((f) => (
              <Field key={f.key} label={f.label}>
                {f.type === 'select' ? (
                  <Select
                    value={form[f.key]}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                    required={f.required}
                  >
                    <option value="">— اختر —</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    type={f.type}
                    value={form[f.key]}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                    required={f.required}
                    min={f.min}
                    max={f.max}
                  />
                )}
              </Field>
            ))}
            <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
              <Btn variant="ghost" size="sm" type="button" onClick={() => setShowAdd(false)}>إلغاء</Btn>
              <Btn variant="primary" size="sm" icon="check" type="submit" loading={addMut.isPending}>
                إضافة
              </Btn>
            </div>
          </form>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
              <tr>
                {itemColumns.map((c) => (
                  <th key={c.key} className="text-start font-semibold px-3 py-2 whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
                <th className="text-end font-semibold px-3 py-2 whitespace-nowrap">
                  إجراءات
                </th>
              </tr>
            </thead>
            <tbody className="divide-soft">
              {items.length === 0 && (
                <tr>
                  <td colSpan={itemColumns.length + 1} className="p-8 text-center text-ink-400">
                    {emptyMsg}
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                  {itemColumns.map((c) => {
                    let v = c.render ? c.render(item) : item[c.key];
                    if (c.money) v = fmtMoney(v);
                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-2 ${c.mono ? 'font-mono text-[11px] text-ink-400' : ''} ${
                          c.money ? 'tabular font-semibold' : ''
                        } ${c.positive ? 'text-emerald-600' : c.negative ? 'text-rose-600' : ''}`}
                      >
                        {c.badge ? (
                          <Badge tone={item.status === 'approved' ? 'success' : item.status === 'rejected' ? 'danger' : 'warn'} dot>
                            {STATUS_LABEL_AR[item.status]}
                          </Badge>
                        ) : v}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-end whitespace-nowrap">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => openEdit(item)}
                        title="تعديل"
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800"
                      >
                        <Icon name="edit" size={14} />
                      </button>
                      <button
                        onClick={() => setDeleting(item)}
                        title="حذف"
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-100 dark:hover:bg-rose-950/30 text-rose-600"
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit modal */}
      {editing && (
        <Modal
          open
          onClose={() => setEditing(null)}
          title={`تعديل ${title}`}
          size="md"
          footer={
            <>
              <Btn variant="ghost" onClick={() => setEditing(null)}>إلغاء</Btn>
              <Btn
                variant="primary"
                icon="check"
                type="submit"
                form="financial-edit-form"
                loading={editMut.isPending}
              >
                حفظ التعديلات
              </Btn>
            </>
          }
        >
          <form id="financial-edit-form" onSubmit={handleEditSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {addFields.map((f) => (
              <Field key={f.key} label={f.label}>
                {f.type === 'select' ? (
                  <Select
                    value={editing.form[f.key] ?? ''}
                    onChange={(e) =>
                      setEditing((s) => ({ ...s, form: { ...s.form, [f.key]: e.target.value } }))
                    }
                    required={f.required}
                  >
                    <option value="">— اختر —</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    type={f.type}
                    value={editing.form[f.key] ?? ''}
                    onChange={(e) =>
                      setEditing((s) => ({ ...s, form: { ...s.form, [f.key]: e.target.value } }))
                    }
                    required={f.required}
                    min={f.min}
                    max={f.max}
                  />
                )}
              </Field>
            ))}
          </form>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleting && (
        <Modal
          open
          onClose={() => setDeleting(null)}
          title={`حذف ${title}`}
          size="sm"
          footer={
            <>
              <Btn variant="ghost" onClick={() => setDeleting(null)}>إلغاء</Btn>
              <Btn
                variant="danger"
                icon="trash"
                loading={delMut.isPending}
                onClick={() => delMut.mutate(deleting.id)}
              >
                تأكيد الحذف
              </Btn>
            </>
          }
        >
          <p className="text-sm">
            هل تريد حذف هذا البند؟ <span className="font-mono text-[11px] text-ink-400">{deleting.id}</span>
          </p>
          <p className="text-xs text-rose-600 mt-2">هذا الإجراء لا يمكن التراجع عنه.</p>
        </Modal>
      )}
    </div>
  );
}

// ─── Helpers ───

function InfoGrid({ items }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((it, i) => (
        <div key={i} className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wider text-ink-400 font-bold">{it.label}</dt>
          <dd
            className={`font-semibold text-sm mt-1 truncate ${it.mono ? 'font-mono' : ''}`}
            dir={it.mono ? 'ltr' : 'rtl'}
            title={it.value}
          >
            {it.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function MiniStat({ label, value, sub, tone = 'neutral' }) {
  const tones = {
    neutral: 'text-ink-700 dark:text-ink-100',
    brand: 'text-brand-600 dark:text-brand-300',
    success: 'text-emerald-600 dark:text-emerald-400',
    warn: 'text-amber-600 dark:text-amber-400',
    info: 'text-sky-600 dark:text-sky-400',
    danger: 'text-rose-600 dark:text-rose-400',
  };
  return (
    <div className="p-3 rounded-xl bg-ink-50 dark:bg-ink-800/40">
      <div className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">{label}</div>
      <div className={`text-xl font-black mt-0.5 tabular ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-ink-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function EditModal({ employee, departments, shifts, onClose, onSave, loading }) {
  const [form, setForm] = useState({
    name: employee.name,
    phone: employee.phone ?? '',
    address: employee.address ?? '',
    position: employee.position,
    deptId: employee.deptId,
    shiftId: employee.shiftId,
    baseSalary: employee.baseSalary,
    allowance: employee.allowance,
    insuranceAmount: employee.insuranceAmount,
    status: employee.status,
  });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={`تعديل: ${employee.name}`}
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
          <Btn type="submit" form="profile-edit" variant="primary" icon="check" loading={loading}>
            حفظ التغييرات
          </Btn>
        </>
      }
    >
      <form
        id="profile-edit"
        onSubmit={(e) => {
          e.preventDefault();
          onSave(form);
        }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <Field label="الاسم"><Input value={form.name} onChange={upd('name')} required /></Field>
        <Field label="الهاتف"><Input value={form.phone} onChange={upd('phone')} dir="ltr" /></Field>
        <Field label="القسم">
          <Select value={form.deptId} onChange={upd('deptId')}>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.nameAr}</option>)}
          </Select>
        </Field>
        <Field label="الوردية">
          <Select value={form.shiftId} onChange={upd('shiftId')}>
            {shifts.map((s) => <option key={s.id} value={s.id}>{s.nameAr}</option>)}
          </Select>
        </Field>
        <Field label="المسمى الوظيفي"><Input value={form.position} onChange={upd('position')} required /></Field>
        <Field label="الحالة">
          <Select value={form.status} onChange={upd('status')}>
            <option value="active">نشط</option>
            <option value="probation">تحت التجربة</option>
            <option value="leave">إجازة</option>
            <option value="terminated">منتهية الخدمة</option>
          </Select>
        </Field>
        <Field label="الراتب الأساسي"><Input type="number" value={form.baseSalary} onChange={upd('baseSalary')} required /></Field>
        <Field label="البدلات"><Input type="number" value={form.allowance} onChange={upd('allowance')} /></Field>
        <Field label="التأمين"><Input type="number" value={form.insuranceAmount} onChange={upd('insuranceAmount')} /></Field>
        <div className="sm:col-span-2">
          <Field label="العنوان"><Input value={form.address} onChange={upd('address')} /></Field>
        </div>
      </form>
    </Modal>
  );
}
